import React from "react";
import type { Theme } from "../lib/theme";
import * as Icon from "../components/OEIcons";

// Project activity feed — reads via the get_project_audit_log RPC
// (migration 024), which joins actor names server-side and enforces
// project access. Shows field-level detail: who changed what, from → to.

type Entry = {
  id: string;
  created_at: string;
  action: string;
  metadata: any;
  actor_name: string;
  actor_email: string | null;
};

const FIELD_LABELS: Record<string, string> = {
  unitCost: "Unit Cost", qty: "Qty", unit: "Unit", description: "Description",
  notes: "Notes", costStatus: "Status", altType: "Alt Type", proposal: "Package",
  included: "Included", overhead: "Overhead %", profit: "Profit %", contingency: "Contingency %",
};

const MONEY_FIELDS = new Set(["unitCost", "old", "new"]);

function fmtVal(field: string | undefined, v: any): string {
  if (v == null || v === "") return "—";
  if (field === "unitCost") {
    const n = parseFloat(v);
    if (!isNaN(n)) return n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 });
  }
  return String(v);
}

export default function ActivityLogPane({T,projectId,user}:{T:Theme;projectId:string|null;user:{id:string;email:string;displayName:string}|null}){
  const [entries,setEntries]=React.useState<Entry[]>([]);
  const [loading,setLoading]=React.useState(true);
  const [error,setError]=React.useState<string|null>(null);

  React.useEffect(()=>{
    let cancelled=false;
    (async()=>{
      setLoading(true);setError(null);
      try{
        const {supabase}=await import("../lib/supabase");
        if(!supabase||!projectId){setLoading(false);return;}
        const{data,error:err}=await supabase.rpc("get_project_audit_log",{
          p_project_id:projectId,
          p_limit:100,
        });
        if(!cancelled){
          if(err)setError(err.message);
          else setEntries(data||[]);
        }
      }catch(ex:any){if(!cancelled)setError(ex.message||"Failed to load");}
      finally{if(!cancelled)setLoading(false);}
    })();
    return()=>{cancelled=true;};
  },[projectId]);

  const fmtAction=(action:string,meta:any)=>{
    const m=meta||{};
    const itemPart=m.item_desc?` on “${m.item_desc}”`:"";
    if(action==="item.update"){
      const f=FIELD_LABELS[m.field]||m.field||"value";
      if(m.old!=null||m.new!=null){
        return `Changed ${f}${itemPart}: ${fmtVal(m.field,m.old)} → ${fmtVal(m.field,m.new)}`;
      }
      return `Updated ${f}${itemPart}`;
    }
    if(action==="item.create")return`Added line item${itemPart}`;
    if(action==="item.delete")return`Deleted line item${itemPart}`;
    if(action==="markup.update"){
      const f=FIELD_LABELS[m.field]||m.field||"markup";
      return `Changed ${f}: ${fmtVal(m.field,m.old)} → ${fmtVal(m.field,m.new)}`;
    }
    if(action==="bid.accept")return`Accepted bid price${itemPart}`;
    if(action==="bid.award")return`Awarded bid${itemPart}`;
    if(action==="snapshot.create")return"Created snapshot";
    if(action==="snapshot.restore")return"Restored snapshot";
    if(action==="snapshot.delete")return"Deleted snapshot";
    if(action==="snapshot.rename")return"Renamed snapshot";
    if(action==="project.create")return"Created project";
    if(action==="project.save")return"Saved project";
    if(action==="project.update")return"Updated project settings";
    if(action==="share.create")return"Shared project";
    if(action==="share.revoke")return"Revoked share";
    if(action==="export.csv")return"Exported CSV";
    if(action==="export.pdf")return"Exported PDF";
    if(action==="export.docx")return"Exported Word document";
    if(action==="export.procore")return"Exported to Procore";
    return action.replace(/\./g," ");
  };
  const timeAgo=(iso:string)=>{
    const s=Math.floor((Date.now()-new Date(iso).getTime())/1000);
    if(s<60)return"just now";
    if(s<3600)return`${Math.floor(s/60)}m ago`;
    if(s<86400)return`${Math.floor(s/3600)}h ago`;
    if(s<604800)return`${Math.floor(s/86400)}d ago`;
    return new Date(iso).toLocaleDateString();
  };

  if(loading)return<div style={{padding:20,color:T.textWeak,fontSize:12}}>Loading activity…</div>;
  if(error)return<div style={{padding:20,color:T.negative,fontSize:12}}>Error: {error}</div>;
  if(!projectId)return<div style={{padding:20,color:T.textWeak,fontSize:12}}>Open a project to see its activity log.</div>;
  if(!entries.length)return(
    <div style={{textAlign:"center",padding:"40px 20px",color:T.textWeak}}>
      <div style={{fontSize:32,marginBottom:12}}><Icon.Clipboard size={32}/></div>
      <div style={{fontSize:14,fontWeight:600,color:T.textMuted,marginBottom:6}}>No activity yet</div>
      <div style={{fontSize:12}}>Changes to this project will appear here.</div>
    </div>
  );
  return(
    <div style={{border:`1px solid ${T.borderPrimary}`,borderRadius:6,overflow:"hidden"}}>
      {entries.map((e,i)=>(
        <div key={e.id} style={{display:"flex",alignItems:"flex-start",gap:14,padding:"12px 16px",
          borderBottom:i<entries.length-1?`1px solid ${T.borderPrimary}`:"none",
          background:i%2===0?T.bgPanel:"transparent"}}>
          <div style={{width:28,height:28,borderRadius:"50%",background:T.bgToast,border:`1px solid ${T.borderSecondary}`,
            display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,
            color:T.textMuted,flexShrink:0,marginTop:1}}>
            {(e.actor_name||"?")[0].toUpperCase()}
          </div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:12,color:T.textSecondary,fontWeight:500}}>{fmtAction(e.action,e.metadata)}</div>
            <div style={{fontSize:11,color:T.textWeak,marginTop:2}}>{e.actor_name||e.actor_email||"Unknown user"} · {timeAgo(e.created_at)}</div>
          </div>
          <div style={{fontSize:10,color:T.textWeak,flexShrink:0,paddingTop:2}}>
            {new Date(e.created_at).toLocaleString([],{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"})}
          </div>
        </div>
      ))}
    </div>
  );
}
